import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { Provider } from "@/utils/types";
import { useCredentialStore } from "./credential";
import { ubiquAxios } from "@/main";

type SortKey = keyof Provider | 'auth_credentials.length' | 'count.asset_count' | 'count.derived_asset_count' | 'count.ppk_count';

export const useProviderStore = defineStore("provider", () => {
  const currentProvider = ref<Provider | null>(null);
  const allProviders = ref<Provider[]>([]);
  const displayedProviders = ref<Provider[]>([]);
  const currentPage = ref(1);
  const itemsPerPage = ref(10);

  const sortConfig = ref({
    key: 'created' as SortKey,
    order: 'asc' as 'asc' | 'desc'
  })

  const totalProviders = computed(() => allProviders.value.length)

  const fetchProviders = async () => {
    if (allProviders.value.length > 0) {
      return;
    }
    try {
      const response = await ubiquAxios.get<Provider[]>("/rse_provider/");
      allProviders.value = response.data;
      await fetchAssetcount();
    } catch (error) {
      console.error("Error fetching providers: ", error);
      allProviders.value = [];
    }
  };

  const fetchAssetcount = async () => {
    try {
      const responseBody = {
        rse_provider_ids: allProviders.value.map((provider) => provider.uuid),
        types: ["asset", "derived_asset", "ppk"],
      };

      const response = await ubiquAxios.post<{ uuid: string; count: Provider["count"] }[]>(
        "/rse/count_for_rse_providers/",
        responseBody
      )
      allProviders.value = allProviders.value.map((provider) => {
        const countData = response.data.find((item) => item.uuid === provider.uuid);
        return {
          ...provider,
          count: countData ? countData.count : { asset_count: 0, derived_asset_count: 0, ppk_count: 0 },
        }
      })
      updateDisplayedProviders();
    } catch (error) {
      console.error("Error sending asset count request: ", error)
    }
  }

  const setCurrentProvider = (provider: Provider) => {
    currentProvider.value = provider;
    const credentialStore = useCredentialStore();
    credentialStore.setCredentialUUIDs(provider.auth_credentials);
  };


  const sortProviders = (providers: Provider[]) => {
    return [...providers].sort((a, b) => {
      const { key, order } = sortConfig.value;

      const compareSpecialKeys = () => {
        switch (key) {
          case 'auth_credentials.length':
            return (a.auth_credentials?.length ?? 0) - (b.auth_credentials?.length ?? 0);

          case 'count.asset_count':
            return (a.count?.asset_count ?? 0) - (b.count?.asset_count ?? 0);

          case 'count.derived_asset_count':
            return (a.count?.derived_asset_count ?? 0) - (b.count?.derived_asset_count ?? 0);

          case 'count.ppk_count':
            return (a.count?.ppk_count ?? 0) - (b.count?.ppk_count ?? 0);

          default:
            return compareStandardKeys(a, b, key);
        }
      };
      const compareStandardKeys = (a: Provider, b: Provider, key: keyof Provider) => {
        const valA = a[key];
        const valB = b[key];

        if (typeof valA === 'string' && typeof valB === 'string') {
          return valA.localeCompare(valB);
        }

        if (typeof valA === 'number' && typeof valB === 'number') {
          return valA - valB;
        }

        return 0;
      };

      const result = compareSpecialKeys();
      return order === 'asc' ? result : -result;
    });
  };

  const updateSort = (
    key: SortKey,
    order: 'asc' | 'desc'
  ) => {
    sortConfig.value = { key, order };
    updateDisplayedProviders();
  };

  const updateDisplayedProviders = () => {
    const sortedProviders = sortProviders(allProviders.value);

    const start = (currentPage.value - 1) * itemsPerPage.value;
    displayedProviders.value = sortedProviders.slice(
      start,
      start + itemsPerPage.value
    );
  };

  const updatePagination = (page: number, newItemsPerPage: number) => {
    currentPage.value = page;
    itemsPerPage.value = newItemsPerPage;
    updateDisplayedProviders();
  };

  const resetProvider = () => {
    currentProvider.value = null;
  };

  const updateName = (uuid: string, name: string) => {
    const provider = allProviders.value.find(provider => provider.uuid === uuid);
    if (provider) {
      provider.name = name;
    } else {
      console.warn(`Provider with uuid ${uuid} not found.`)
    }
    updateDisplayedProviders();
  };

  const resetTable = () => {
    sortConfig.value.key = "created";
    sortConfig.value.order = "asc";
    currentPage.value = 1;
    itemsPerPage.value = 10;
  }

  const resetStore = () => {
    allProviders.value = [];
    displayedProviders.value = [];
    currentProvider.value = null;
    sortConfig.value.key = "created";
    sortConfig.value.order = "asc";
    currentPage.value = 1;
    itemsPerPage.value = 10;
  };

  return {
    allProviders,
    displayedProviders,
    totalProviders,
    currentProvider,
    sortConfig,
    currentPage,
    itemsPerPage,
    fetchProviders,
    fetchAssetcount,
    setCurrentProvider,
    updateDisplayedProviders,
    updateSort,
    updatePagination,
    updateName,
    resetProvider,
    resetTable,
    resetStore,
  };
}, {persist: true });
